import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Download, RefreshCw, Boxes, PackageSearch, Search } from 'lucide-react';
import { toast } from 'sonner';
import { productionAPI } from '@/lib/api';

const todayISO = () => new Date().toISOString().slice(0, 10);

function StatTile({ label, value, tone }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${tone || 'bg-white'}`}>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="text-base font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function StockTable({ block, withRm, search, testid }) {
  const rows = (block?.rows || []).filter(r => {
    const q = search.trim().toLowerCase();
    return !q || `${r.unique_id} ${r.item_name}`.toLowerCase().includes(q);
  });
  const t = block?.totals || {};

  return (
    <div className="space-y-3">
      <div className={`grid gap-2 ${withRm ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
        <StatTile label="Free stock" value={t.free_stock ?? 0} />
        {withRm && <StatTile label="From unconsumed RM" value={t.from_raw_material ?? 0} tone="bg-amber-50 border-amber-200" />}
        <StatTile label="Inside higher-level stock" value={t.embedded_in_higher_level ?? 0} />
        <StatTile label="Total equivalent" value={t.total_equivalent ?? 0} tone="bg-emerald-50 border-emerald-200" />
        <StatTile label="In open work orders" value={t.in_open_wos ?? 0} tone="bg-slate-50" />
      </div>

      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-xs" data-testid={testid}>
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-2 py-2">Item</th>
              <th className="text-right px-2 py-2">Free stock</th>
              {withRm && <th className="text-right px-2 py-2">From RM</th>}
              <th className="text-right px-2 py-2">In higher-level stock</th>
              <th className="text-right px-2 py-2">Total equivalent</th>
              <th className="text-right px-2 py-2">In open WOs</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={withRm ? 6 : 5} className="px-2 py-6 text-center text-muted-foreground">
                Nothing in stock for this selection
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.item_id} className="border-t hover:bg-slate-50/60" data-testid={`wip-row-${r.unique_id}`}>
                <td className="px-2 py-2">
                  <div className="font-medium text-slate-800">{r.unique_id}</div>
                  <div className="text-[11px] text-muted-foreground">{r.item_name}</div>
                  {withRm && r.bom_used && (
                    <div className="text-[10px] text-amber-700 mt-0.5">BOM: {r.bom_used}
                      {r.materials?.length > 0 && ` · from ${r.materials.map(m => `${m.stock} ${m.unique_id}`).join(' + ')}`}
                    </div>
                  )}
                  {r.shared_material_with?.length > 0 && (
                    <div className="text-[10px] text-orange-600 mt-0.5">
                      material shared with {r.shared_material_with.join(', ')}
                    </div>
                  )}
                  {r.embedded_sources?.length > 0 && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      held in {r.embedded_sources.join(', ')}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-right">{r.free_stock}</td>
                {withRm && <td className="px-2 py-2 text-right text-amber-700">{r.from_raw_material}</td>}
                <td className="px-2 py-2 text-right">{r.embedded_in_higher_level}</td>
                <td className="px-2 py-2 text-right font-semibold">{r.total_equivalent}</td>
                <td className="px-2 py-2 text-right text-slate-500">{r.in_open_wos}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function WipStockPage() {
  const [asOn, setAsOn] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = async (date) => {
    setLoading(true);
    try {
      const res = await productionAPI.getWipStock(date);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load WIP stock');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(asOn); /* eslint-disable-next-line */ }, []);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await productionAPI.exportWipStock(asOn);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `WIP_Stock_${asOn}.xlsx`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="wip-stock-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">WIP Stock</h1>
          <p className="text-xs text-muted-foreground">
            Everything on hand in {(data?.godowns || []).map(g => g.name).join(', ') || 'Stores, Finished Goods, Production'} —
            expressed as level-1 and bought-out equivalents for the 10-day planning run.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px]">As on opening of</Label>
            <Input type="date" value={asOn} onChange={e => setAsOn(e.target.value)}
              className="h-8 text-xs w-36" data-testid="wip-as-on" />
          </div>
          <Button size="sm" className="h-8 text-xs" onClick={() => load(asOn)} disabled={loading}
            data-testid="wip-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Show
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={exportExcel} disabled={exporting}
            data-testid="wip-export">
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            Excel
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between gap-3">
          <CardTitle className="text-sm flex items-center gap-2">
            Position as on {data?.as_on || asOn}
            <Badge variant="outline" className="text-[10px]">opening balance</Badge>
          </CardTitle>
          <div className="relative w-56">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search part..."
              className="h-7 text-xs pl-7" data-testid="wip-search" />
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-10">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculating…
            </div>
          ) : (
            <Tabs defaultValue="level1">
              <TabsList>
                <TabsTrigger value="level1" className="text-xs" data-testid="wip-tab-level1">
                  <Boxes className="w-3.5 h-3.5 mr-1" /> Manufactured (level 1) · {data?.level1?.totals?.rows || 0}
                </TabsTrigger>
                <TabsTrigger value="bought" className="text-xs" data-testid="wip-tab-bought">
                  <PackageSearch className="w-3.5 h-3.5 mr-1" /> Bought-out · {data?.bought_out?.totals?.rows || 0}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="level1" className="mt-3">
                <StockTable block={data?.level1} withRm search={search} testid="wip-level1-table" />
              </TabsContent>
              <TabsContent value="bought" className="mt-3">
                <StockTable block={data?.bought_out} search={search} testid="wip-bought-table" />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
